import React, { createContext, useContext, useMemo } from "react"

import { useEvent } from "shared/lib/useEvent"
import { ApiResponse } from "shared/types/ApiResponse"
import { FilePreview } from "shared/types/File"
import { AttachmentType } from "shared/types/__generated__/graphql"
import { FileCard, FileCardProps } from "shared/ui/file-card/FileCard"
import { FileUpload } from "shared/ui/file-upload/FileUpload"
import { FileUploadPropsContext } from "shared/ui/file-upload/FileUploadPropsContext"
import { Toggle } from "shared/ui/input/boolean/toggle/Toggle"

import "./styles/attachment-link-form.scss"
import { AttachmentLinkFormViewModel } from "./types/AttachmentLinkFormViewModel"

const AttachmentLinkFormContext = createContext<{
  getName: (attachment: AttachmentType) => string
}>({ getName: attachment => attachment.id })

export const AttachmentLinkFormView = React.memo(
  ({
    attachmentsByCategory,
    categories,
    isReadonly,
    getName,
    onUpload
  }: AttachmentLinkFormViewModel) => {
    const contextValue = useMemo(() => {
      return { getName }
    }, [getName])

    return (
      <div className="attachment-link-form__categories">
        {categories?.map(category => {
          if (
            isReadonly &&
            (!attachmentsByCategory[category.value] ||
              attachmentsByCategory[category.value].length === 0)
          ) {
            return null
          }
          return (
            <div
              className="attachment-link-form__category"
              key={category.value}
            >
              <h3 className="attachment-link-form__category-title label label--bold">
                {category.label}
              </h3>
              <AttachmentLinkFormContext.Provider value={contextValue}>
                <AttachmentLinkFormCategoryUpload
                  attachments={attachmentsByCategory[category.value]}
                  category={category.value}
                  onUpload={onUpload}
                  isReadonly={isReadonly}
                />
              </AttachmentLinkFormContext.Provider>
            </div>
          )
        })}
      </div>
    )
  }
)

function AttachmentLinkFormCategoryUpload({
  attachments,
  category,
  onUpload,
  isReadonly
}: {
  attachments: AttachmentType[]
  category: string
  onUpload?: (
    file: FilePreview,
    category: string
  ) => Promise<ApiResponse<AttachmentType>> | void
  isReadonly?: boolean
}) {
  const handleUpload = useEvent((file: FilePreview) => {
    return onUpload?.(file, category)
  })

  return (
    <FileUpload.Root
      files={attachments}
      multiple
      onUpload={handleUpload}
      FileCardComponent={AttachmentLinkFormFileCard}
      isReadonly={isReadonly || !onUpload}
    >
      <FileUpload.List />
      <FileUpload.Input variant="minimal" />
    </FileUpload.Root>
  )
}

function AttachmentLinkFormFileCard(
  props: FileCardProps & { attachment?: AttachmentType }
) {
  const { isReadonly } = useContext(FileUploadPropsContext)
  const { attachment } = props
  const { getName } = useContext(AttachmentLinkFormContext)

  if (!attachment) {
    return null
  }

  return (
    <FileCard
      {...props}
      slot={
        isReadonly ? null : (
          <Toggle.Root name={getName(attachment)}>
            <Toggle.Input />
            <Toggle.Label />
          </Toggle.Root>
        )
      }
    />
  )
}
